/**
 * Homepage copy, taken from the approved landing design and the company
 * profile PDF. Kept as data so a wording change is a one-line diff.
 */

export const HERO = {
  badge: 'Live farm sensor monitoring',
  /*
   * The accent half renders in amber, so the split falls where the promise
   * lands rather than mid-clause.
   *
   * "farm records", not "farm's records" or "farms' records". Here `farm` is
   * attributive, the way it works in "farm equipment", so the phrase reads
   * correctly whether the visitor runs one house or five. An apostrophe would
   * force a choice between singular and plural and be wrong for half the
   * audience.
   */
  headline: 'Manage, Monitor, and Keep Your Farm Records',
  headlineAccent: 'in One Place.',
  sub: 'No more paper logs, forgotten vaccines, or guessing your profit per flock. Message us on WhatsApp and we will set up your farm on Chickrah.',
  cta: 'Chat with us on WhatsApp',
};

/** Stats band overlapping the hero. These are public performance claims. */
export const STATS = [
  { value: 'Up to 15%', label: 'Feed Savings' },
  { value: '95%', label: 'Egg Production Accuracy' },
  { value: '30%', label: 'Mortality Reduction' },
  { value: '100%', label: 'Vaccination Compliance' },
];

export const TRUST = {
  heading: 'Trusted by farms across Kenya & Uganda',
  // Where the farms are is the trust signal for this market, so that is the
  // half of the sentence the accent marks.
  accent: 'Kenya & Uganda',
  sub: 'Chickrah is the precision management platform powering real poultry operations, not a hypothetical feature list.',
  badges: ['TAFIS', 'LOKO HARVEST'],
  more: '+ farms across East Africa',
};

/**
 * The Monitor / Alert / Manage thesis — the spine of the whole product story.
 * One idea said three ways.
 */
export const TESTIMONIAL_FALLBACK = {
  quote:
    'Before Chickrah, we relied on spreadsheets and notebooks, leading to errors and losses. With Chickrah, our data is captured in real time, improving accuracy, decision-making, professionalism, and overall farm stability.',
  name: 'Ann Mbabazi',
  role: 'CEO, Loko Harvest · Entebbe, Uganda',
};

export const MOVEMENT = {
  heading: 'Built with farmers, for farms',
  sub: "Chickrah isn't competing against other software. It's competing against the paper log, the WhatsApp message, and the physical site visit.",
  columns: [
    {
      title: 'By farmers, for farms',
      body: 'Built from what poultry farm owners actually need to see every day, not a hypothetical feature list from a boardroom.',
    },
    {
      title: 'More trustworthy than memory',
      body: 'More precise than a paper log, more reliable than a phone call to the farm manager, without drifting into consumer-app playfulness.',
    },
    {
      title: 'Grow with confidence',
      body: 'Clear reports on profit and performance mean you can scale to your next farm knowing the numbers are real.',
    },
  ],
};

/** Comparison table — the real competitor is paper, not other software. */
export const COMPARISON = {
  eyebrow: 'One system',
  /*
   * Split so the pivot word can be coloured. Quotation marks were tried around
   * each side and read as scare quotes — as though neither label were meant
   * seriously. Colour separates the two halves without that implication.
   */
  heading: { left: 'How it is done today', vs: 'vs', right: 'Chickrah' },
  sub: 'Paper logs, spreadsheets, memory. Or one system that does all 7 jobs for you.',
  columns: ['Feature', 'How it is done today', 'Chickrah'],
  /*
   * `replaces` is the manual habit the farm uses now, not a competing product.
   * Deliberately no cost column: the alternatives here are paper and memory, and
   * putting a monthly price on those would be inventing a number.
   */
  rows: [
    {
      icon: 'sensor',
      feature: 'Sensor environmental monitoring',
      replaces: 'Manual walk-throughs',
      todayIcon: 'steps',
    },
    {
      icon: 'feed',
      feature: 'Feed consumption tracking',
      replaces: 'Paper feed logs',
      todayIcon: 'paper',
    },
    {
      icon: 'ledger',
      feature: 'Feed ingredient ledger',
      replaces: 'Spreadsheets',
      todayIcon: 'spreadsheet',
    },
    {
      icon: 'egg',
      feature: 'Egg production tracking',
      replaces: 'Manual tally sheets',
      todayIcon: 'tally',
    },
    {
      icon: 'mortality',
      feature: 'Mortality rate tracking',
      replaces: 'Memory & notebooks',
      todayIcon: 'notebook',
    },
    {
      icon: 'weight',
      feature: 'Weight & growth monitoring',
      replaces: 'Periodic manual checks',
      todayIcon: 'clock',
    },
    {
      icon: 'vaccination',
      feature: 'Vaccination management',
      replaces: 'WhatsApp reminders',
      todayIcon: 'whatsapp',
    },
  ],
};

export const RESULTS = {
  heading: 'What to Expect After Using Chickrah',
  accent: 'Expect',
  chips: [
    { value: 'Up to 15%', label: 'Feed Savings' },
    { value: '100%', label: 'Feed Stock Visibility' },
    { value: '95%', label: 'Egg Production Accuracy' },
    { value: '30%', label: 'Mortality Reduction' },
    { value: '98%', label: 'Weight Tracking Accuracy' },
    { value: '100%', label: 'Vaccination Compliance' },
    { value: 'Real-Time', label: 'Environmental Alerts' },
    { value: '24/7', label: 'Farm Sensor Monitoring' },
  ],
};

export const IN_ACTION = {
  // The pictures are the argument. Copy here only has to say where they were
  // taken and get out of the way; describing the photographs to someone about
  // to look at them is wasted breath.
  heading: 'See It On Real Farms',
  accent: 'Real Farms',
  sub: 'Filmed in Kenya and Uganda.',
  empty: 'Farm photos and videos are on their way.',
};
